import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";

import api from "api/index";

const round1 = n => Math.round(n * 10) / 10;

const buildColumns = retakeMap => [
  { id: "semester", header: "Semester", accessorFn: e => e.semester?.name || "—" },
  { id: "code", header: "Code", accessorFn: e => e.course?.code || "—" },
  { id: "course", header: "Course", accessorFn: e => e.course?.name || "—" },
  {
    id: "cw",
    header: "CW (/60)",
    accessorFn: e => {
      const r = e.ExamResult;
      if (!r) return "—";
      return round1((r.ca1Score || 0) * 0.3 + (r.ca2Score || 0) * 0.2 + (r.practicalScore || 0) * 0.1);
    },
    meta: { align: "right" }
  },
  {
    id: "exam",
    header: "Exam (/40)",
    accessorFn: e => (e.ExamResult ? round1((e.ExamResult.examScore || 0) * 0.4) : "—"),
    meta: { align: "right" }
  },
  {
    id: "total",
    header: "Total",
    accessorFn: e => (e.ExamResult?.totalScore != null ? `${e.ExamResult.totalScore}%` : "—"),
    meta: { align: "right" }
  },
  { id: "grade", header: "Grade", accessorFn: e => e.ExamResult?.grade || "—" },
  {
    id: "status",
    header: "Status",
    cell: info => {
      const e = info.row.original;
      const status = e.ExamResult?.status || "pending";
      const attemptNumber = retakeMap.get(`${e.course?.id}_${e.semester?.id}`);
      return (
        <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          <Pill tone={status === "final" ? "success" : "warning"}>{status}</Pill>
          {attemptNumber != null && <Pill tone="danger">Retake #{attemptNumber}</Pill>}
        </div>
      );
    }
  }
];

class PortalResults extends Component {
  state = {
    enrollments: [], retakes: [], loading: true,
    filterYear: "", filterSemester: "", filterCourse: ""
  };

  componentDidMount() {
    this.fetchResults();
  }

  fetchResults = async () => {
    try {
      const [resultsRes, retakesRes] = await Promise.all([
        api.get("/portal/results"),
        api.get("/portal/retakes").catch(() => ({ data: { data: [] } }))
      ]);
      const d = resultsRes.data.data || resultsRes.data;
      const r = retakesRes.data.data || retakesRes.data;
      this.setState({
        enrollments: Array.isArray(d) ? d : d.data || [],
        retakes: Array.isArray(r) ? r : [],
        loading: false
      });
    } catch (e) {
      this.setState({ enrollments: [], retakes: [], loading: false });
    }
  };

  render() {
    const { history } = this.props;
    const { enrollments, retakes, loading, filterYear, filterSemester, filterCourse } = this.state;

    const retakeMap = new Map(retakes.map(r => [`${r.courseId}_${r.semesterId}`, r.attemptNumber]));
    const columns = buildColumns(retakeMap);

    const years = [];
    const semesters = [];
    const courses = [];
    enrollments.forEach(e => {
      const y = e.semester?.academicYear;
      if (y && !years.find(x => x.id === y.id)) years.push(y);
      const s = e.semester;
      if (s && !semesters.find(x => x.id === s.id)) semesters.push(s);
      const c = e.course;
      if (c && !courses.find(x => x.id === c.id)) courses.push(c);
    });

    const filtered = enrollments.filter(e =>
      (!filterYear || e.semester?.academicYear?.id === filterYear) &&
      (!filterSemester || e.semester?.id === filterSemester) &&
      (!filterCourse || e.course?.id === filterCourse)
    );

    return (
      <GridContainer>
        <GridItem xs={12}>
          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", marginBottom: "16px", alignItems: "flex-end" }}>
            <FormControl style={{ minWidth: "180px" }}>
              <InputLabel>Academic Year</InputLabel>
              <Select value={filterYear} onChange={e => this.setState({ filterYear: e.target.value })}>
                <MenuItem value="">— All Years —</MenuItem>
                {years.map(y => <MenuItem key={y.id} value={y.id}>{y.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl style={{ minWidth: "180px" }}>
              <InputLabel>Semester</InputLabel>
              <Select value={filterSemester} onChange={e => this.setState({ filterSemester: e.target.value })}>
                <MenuItem value="">— All Semesters —</MenuItem>
                {semesters.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl style={{ minWidth: "220px" }}>
              <InputLabel>Course</InputLabel>
              <Select value={filterCourse} onChange={e => this.setState({ filterCourse: e.target.value })}>
                <MenuItem value="">— All Courses —</MenuItem>
                {courses.map(c => <MenuItem key={c.id} value={c.id}>{c.code} — {c.name}</MenuItem>)}
              </Select>
            </FormControl>
            <Button color="primary" onClick={() => history.push("/portal/statement")} style={{ marginLeft: "auto" }}>
              Statement of Results
            </Button>
          </div>

          <DataTable
            title="My Results"
            subtitle="Academic results across all semesters"
            columns={columns}
            data={filtered}
            isLoading={loading}
            emptyState="No results available yet."
          />
        </GridItem>
      </GridContainer>
    );
  }
}

PortalResults.propTypes = { history: PropTypes.object.isRequired };
export default withRouter(PortalResults);
